import type { OneMedia } from '@prismamedia/one-components';
import type { RawDraftContentState } from 'draft-js';
import {
  ArticleStatus,
  GetArticleQuery_query_article_Article,
  GetArticleQuery_query_article_Article_tags_ArticleTag,
  GetArticleQuery_query_article_Article_events_ArticleEvent,
  ArticleFormat,
} from '../../__generated__/queries-web';

export interface ArticleState
  extends Omit<
    GetArticleQuery_query_article_Article,
    'lead' | 'body' | 'medias'
  > {
  lead: RawDraftContentState | null;
  body: RawDraftContentState | null;
  medias: OneMedia[] | null;
  articleChanged: boolean;
  newStatus: ArticleStatus | null;
  transitionType?: string | null;
}

enum ArticleEditActionTypeEnum {
  CHANGE_STATUS = 'CHANGE_STATUS',
  INIT_ARTICLE_DATA = 'INIT_ARTICLE_DATA',
  INIT_BY_LOCALSTORAGE = 'INIT_BY_LOCALSTORAGE',
  SET_ARTICLE_TAGS = 'SET_ARTICLE_TAGS',
  UPDATE_FIELD = 'UPDATE_FIELD',
  UPDATE_PUBLISH_DATE_AND_STATUS = 'UPDATE_PUBLISH_DATE_AND_STATUS',
}

export interface ArticleEditAction {
  type: ArticleEditActionTypeEnum;
  data: any;
}

type ArticleEditActionsCreators = Record<
  string,
  { (payload: any): ArticleEditAction }
>;

export const initState = (articleData: any): ArticleState => ({
  ...articleData,
  lead: articleData.lead ? articleData.lead : null,
  body: articleData.body ? articleData.body : null,
  medias: articleData.medias ? articleData.medias : null,
  articleChanged: false,
  newStatus: null,
  transitionType: null,
  canonical: articleData.canonical ?? null,
  authors:
    articleData.format !== ArticleFormat.Video && articleData.authors[0]?.author
      ? articleData.authors
      : [],
});

export interface SetArticleTagsActionPayload {
  field: 'events' | 'tags';
  value: (
    | GetArticleQuery_query_article_Article_events_ArticleEvent
    | GetArticleQuery_query_article_Article_tags_ArticleTag
  )[];
}

export const actions: ArticleEditActionsCreators = {
  changeStatus: ({
    newStatus,
    transitionType,
  }: {
    newStatus: ArticleStatus;
    transitionType?: string;
  }) => ({
    type: ArticleEditActionTypeEnum.CHANGE_STATUS,
    data: { newStatus, transitionType },
  }),
  initArticleData: ({
    articleData,
  }: {
    articleData: GetArticleQuery_query_article_Article;
  }) => ({
    type: ArticleEditActionTypeEnum.INIT_ARTICLE_DATA,
    data: { articleData },
  }),
  setArticleTags: ({ field, value }: SetArticleTagsActionPayload) => ({
    type: ArticleEditActionTypeEnum.SET_ARTICLE_TAGS,
    data: {
      field,
      value,
    },
  }),
  updateField: ({ field, value }: { field: string; value: any }) => ({
    type: ArticleEditActionTypeEnum.UPDATE_FIELD,
    data: {
      field,
      value,
      articleChanged: !(
        field === 'medias' ||
        field === 'lead' ||
        field === 'body'
      ),
    },
  }),
  updatePublishDateAndStatus: ({
    publishDate,
    status,
  }: {
    publishDate?: string;
    status: ArticleStatus;
  }) => ({
    type: ArticleEditActionTypeEnum.UPDATE_PUBLISH_DATE_AND_STATUS,
    data: { publishDate, status },
  }),
};

export const reducer = (
  state: ArticleState,
  action: ArticleEditAction,
): ArticleState => {
  switch (action.type) {
    case ArticleEditActionTypeEnum.INIT_ARTICLE_DATA: {
      return initState(action.data.articleData);
    }

    case ArticleEditActionTypeEnum.SET_ARTICLE_TAGS:
    case ArticleEditActionTypeEnum.UPDATE_FIELD:
      return {
        ...state,
        [action.data.field]: action.data.value,
        articleChanged: true,
      };

    case ArticleEditActionTypeEnum.CHANGE_STATUS: {
      return {
        ...state,
        newStatus: action.data.newStatus,
        transitionType: action.data.transitionType,
      };
    }

    case ArticleEditActionTypeEnum.UPDATE_PUBLISH_DATE_AND_STATUS: {
      return {
        ...state,
        status: action.data.status,
        publishedAt: action.data.publishDate,
        newStatus: null,
        transitionType: null,
      };
    }

    default:
      return state;
  }
};
